import { useId } from 'react';
import RequiredIndicator from '../../common/required-indicator/required-indicator.component';
import { DynamicFormRequiredType } from '../../dynamic-form/dynamic-form.enums';
import ErrorLabel from '../../labels/error-label';
import { FieldDescription } from '../../labels/field-description';
import FieldLabel from '../../labels/field-label';
import { MultiCombobox, type MultiComboboxProps } from './multi-combobox';
import { SingleCombobox, type SingleComboboxProps } from './single-combobox';

interface ComboboxFieldProps {
  /** @deprecated Compose `Field` and `FieldLabel` around the combobox instead. */
  label?: string;
  /** @deprecated Pass `aria-required` and mark the `FieldLabel` as required instead. */
  required?: DynamicFormRequiredType;
  readOnly?: boolean;
  /** @deprecated Pass `aria-invalid` and render a `FieldError` instead. */
  error?: string;
  'aria-invalid'?: boolean;
  /** Helper text rendered below the combobox and wired to the input via `aria-describedby`. */
  description?: string;
}

// The discriminant lives here rather than on the presets, which know nothing about this union. It is mandatory on
// the multi arm: optional, array-shaped props would match it while the dispatch below fell through to single-select.
export type ComboboxProps<T> = ComboboxFieldProps &
  ((MultiComboboxProps<T> & { multiple: true }) | (SingleComboboxProps<T> & { multiple?: false }));

export const Combobox = <T,>({
  label,
  required = DynamicFormRequiredType.False,
  readOnly = false,
  error,
  description,
  ...modeProps
}: ComboboxProps<T>) => {
  const generatedId = useId();
  // A generated id keeps a built-in label associated with the body when the caller supplies none.
  const identifier = modeProps.id !== undefined ? String(modeProps.id) : label ? generatedId : undefined;

  const ariaInvalid = error !== undefined ? true : modeProps['aria-invalid'];

  const descriptionId = description ? `${identifier ?? generatedId}-description` : undefined;
  const describedBy = modeProps['aria-describedby'] ?? descriptionId;

  return (
    <div className="flex flex-col">
      <div className="flex items-center">
        {label && <FieldLabel name={identifier} label={label} />}
        <RequiredIndicator readOnly={readOnly} required={required} />
      </div>
      {modeProps.multiple ? (
        <MultiCombobox
          {...(modeProps as MultiComboboxProps<T>)}
          id={identifier}
          readOnly={readOnly}
          aria-invalid={ariaInvalid}
          aria-describedby={describedBy}
        />
      ) : (
        <SingleCombobox
          {...(modeProps as SingleComboboxProps<T>)}
          id={identifier}
          readOnly={readOnly}
          aria-invalid={ariaInvalid}
          aria-describedby={describedBy}
        />
      )}
      {description && (
        <FieldDescription id={descriptionId} className="py-1">
          {description}
        </FieldDescription>
      )}
      {error && <ErrorLabel>{error}</ErrorLabel>}
    </div>
  );
};
